import { UserResource } from "@clerk/types";
import { Avatar } from "@heroui/react";
import { User } from "assets/icons";
import { Id } from "components/kanbanBoard/types";
import { useEffect, useRef, useState } from "react";
import { useUsers } from "shared/contexts/users.context";
import { ClerkUser } from "shared/types/user";
import { getUserImageUrl } from "shared/utils/users";

type UserSelectionProps = {
  defaultUser: Id | ClerkUser | UserResource | null | undefined;
  onUserSelect: any;
  isDisabled?: boolean;
};

export const UserSelection = ({
  defaultUser,
  onUserSelect,
  isDisabled = false,
}: UserSelectionProps) => {
  const [isUserDropdownOpen, setUserDropdownOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<
    Id | ClerkUser | UserResource | null
  >(null);
  const [userSearchInput, setUserSearchInput] = useState("");
  const { users } = useUsers();
  const userDropdownRef = useRef<HTMLInputElement | null>(null);
  const searchUserRef = useRef<HTMLInputElement | null>(null);

  const handleUserSearchChange = (e: any) => {
    setUserSearchInput(e.target.value);
  };

  const selectUser = (user: ClerkUser) => {
    setSelectedUser(user);
    setUserDropdownOpen(false);
    onUserSelect(user);
  };

  const handleClickOutside = (event: any) => {
    if (
      userDropdownRef.current &&
      !userDropdownRef.current.contains(event.target)
    ) {
      setUserDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (isUserDropdownOpen) {
      searchUserRef.current?.focus();
    }
  }, [isUserDropdownOpen]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedUserImageUrl = getUserImageUrl(selectedUser, users);
  const defaultUserImageUrl = getUserImageUrl(defaultUser, users);

  return (
    <div className="relative">
      <div
        className="flex cursor-pointer items-center gap-2 text-sm"
        onClick={() => setUserDropdownOpen(!isUserDropdownOpen)}
      >
        {selectedUserImageUrl ? (
          <Avatar size="sm" src={selectedUserImageUrl} />
        ) : defaultUserImageUrl ? (
          <Avatar size="sm" src={defaultUserImageUrl} />
        ) : (
          <div className="flex">
            <User className="mr-2 h-[20px] w-[20px]" />
          </div>
        )}
      </div>

      {/* User Dropdown (Modal) */}
      {!isDisabled && isUserDropdownOpen && (
        <div
          ref={userDropdownRef}
          className="shadow-lg absolute left-0 z-10 mt-2 rounded-md bg-white p-4"
        >
          <input
            ref={searchUserRef}
            type="text"
            placeholder="Zoek gebruiker..."
            value={userSearchInput}
            onChange={handleUserSearchChange}
            className="mb-4 p-2"
          />

          {users
            .filter(
              (user) =>
                user.first_name
                  ?.toLowerCase()
                  .includes(userSearchInput.toLowerCase()),
            )
            .map((user) => (
              <div
                key={user.id}
                className="mb-2 flex cursor-pointer items-center justify-between"
                onClick={() => selectUser(user)}
              >
                <div className="mr-3 flex items-center gap-2">
                  <Avatar
                    size="sm"
                    src={user.image_url}
                    name={user.first_name}
                  />
                  <p>
                    {user.first_name} {user.last_name}
                  </p>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
